/*
  Calculations Reference Component
  --------------------------------
  Read-only reference showing ROI calculation formulas.
*/

import React from 'react';

/**
 * Calculations reference panel showing ROI formulas
 */
const CalculationsReference = () => {
  return (
    <div className="space-y-4 text-sm text-gray-800">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Calculations</h2>
        <span className="text-xs text-gray-600">Read-only reference. Future: make editable.</span>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Industry Waste Percent</h3>
        <p className="mb-2 text-gray-700">Maps Automation Maturity (1..10) so 1 ⇒ industry.wasteLow, 10 ⇒ industry.wasteHigh.</p>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`industryWastePercent = industry.wasteLow + ((10 - automationMaturity) / 9) * (industry.wasteHigh - industry.wasteLow)`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Admin Waste (Process Orientation)</h3>
        <p className="mb-2 text-gray-700">Diagnostic mapping used in waste breakdown. 1 ⇒ 30%, 10 ⇒ 20% (linear).</p>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`adminWaste = 0.30 - ((processMaturity - 1) / 9) * 0.10
adminWasteLow  = adminWaste
adminWasteHigh = adminWaste`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Total Waste (diagnostic)</h3>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`totalWaste.low  = annualRevenue * (industry.wasteLow  * adminWasteLow)
totalWaste.high = annualRevenue * (industry.wasteHigh * adminWasteHigh)`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">AI Agents Average (fixed admin 20%/30%)</h3>
        <p className="mb-2 text-gray-700">Baseline used for headline savings and interpolation range.</p>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`aiAdminLow  = 0.20
aiAdminHigh = 0.30
aiAgentsAverage.percentLow  = industry.wasteLow  * aiAdminLow
aiAgentsAverage.percentHigh = industry.wasteHigh * aiAdminHigh
aiAgentsAverage.amountLow   = annualRevenue * aiAgentsAverage.percentLow
aiAgentsAverage.amountHigh  = annualRevenue * aiAgentsAverage.percentHigh`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Potential Annual Savings (interpolated)</h3>
        <p className="mb-2 text-gray-700">Interpolate within [AI base low, AI base high] so (Automation=1, Process=1) ⇒ high end.</p>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`baseLow  = aiAgentsAverage.amountLow
baseHigh = aiAgentsAverage.amountHigh
autoNorm = (10 - automationMaturity) / 9
procNorm = (10 - processMaturity)    / 9
sLow  = min(autoNorm, procNorm)
sHigh = max(autoNorm, procNorm)

potentialSavings.low  = baseLow + (baseHigh - baseLow) * sLow
potentialSavings.high = baseLow + (baseHigh - baseLow) * sHigh`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">FTE Savings (context)</h3>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`avgEmployeeCost = 50000
fteSavings.low  = potentialSavings.low  / avgEmployeeCost
fteSavings.high = potentialSavings.high / avgEmployeeCost`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Agent-driven Impact</h3>
        <p className="mb-2 text-gray-700">Based on selected General + Industry agents with conservative caps.</p>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`// Selected agents
selectedGA = indices → generalAgents
selectedIA = indices → selectedIndustry.agents (if defined)

// Sums
totalTimeSavedFTE     = sum(a.timeSavedFTE)
totalRevenuePotential = sum(a.revenuePotential)

// Caps
timeSavedCap       = 0.60    // max 60% FTE-equivalent time saved
revenueImpactCap   = 0.05    // max +5% revenue impact

timeSavedAdj       = min(totalTimeSavedFTE, timeSavedCap)
revenuePotentialAdj= min(totalRevenuePotential, revenueImpactCap)

// Automation factor for agent impacts
automationFactor         = 0.60 - ((automationMaturity - 1) / 9) * 0.45 // 0.60 → 0.15
fteSavedFromAgents       = employeeCount * timeSavedAdj * automationFactor
directSavingsFromAgents  = annualRevenue * (industryWastePercent * adminWasteLow)
salesCapacityOpportunity = annualRevenue * revenuePotentialAdj * automationFactor

agentImpact.total = directSavingsFromAgents + salesCapacityOpportunity`}</pre>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <h3 className="font-semibold mb-1">Efficiency & RPE</h3>
        <pre className="whitespace-pre-wrap break-all text-xs bg-white border rounded p-2">{`autoScore  = ((11 - automationMaturity) / 10) * 50
procScore  = ((11 - processMaturity)  / 10) * 50
efficiencyScore = round(autoScore + procScore)

revenuePerEmployee = annualRevenue / max(employeeCount, 1)`}</pre>
      </div>

      <p className="text-xs text-gray-600">
        Source of truth: see logic in <code>src/components/ROICalculator.js</code> and datasets in{' '}
        <code>src/data/industryTasks.js</code>, <code>src/data/industryDataComplete.js</code>,{' '}
        <code>src/data/industryRPE.js</code>.
      </p>
    </div>
  );
};

export default CalculationsReference;
